// src/pages/About.jsx
import React, { useEffect, useMemo, useState } from "react";

// Assets
import personImage1 from "../assets/sir2.png";
import softskill from "../assets/softskills.jpg";
import technical from "../assets/technical.png";
import logo from "../assets/growmaxlogo11.png";
import leader from "../assets/leadersir.jpeg";
import journy from "../assets/journy.jpg";
import banner1 from "../assets/banner1.jpeg";
import banner2 from "../assets/banner2.jpeg";
import banner3 from "../assets/Image.jpeg"; // ⬅️ renamed (was `Image`)
import Award from "../assets/Award.jpeg";

const slides = [banner1, banner2, banner3, Award];

function Section({
  reverse = false,
  title,
  subtitle,
  paragraphs = [],
  bullets = [],
  image,
  imageAlt,
  imageClassName = "",
  imageStyle = {},
  imageFit = "cover",
}) {
  const fitClass = imageFit === "contain" ? "object-contain" : "object-cover";
  return (
    <section
      className={`grid items-center gap-8 md:gap-12 lg:gap-14 ${
        reverse ? "lg:grid-cols-[1.05fr_1fr]" : "lg:grid-cols-[1fr_1.05fr]"
      } grid-cols-1`}
    >
      {/* Text */}
      <div className={`${reverse ? "lg:order-2" : ""}`}>
        {subtitle && (
          <p className="text-xs tracking-[0.25em] uppercase text-sky-600 font-semibold mb-2">
            {subtitle}
          </p>
        )}
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-tight">
          <span className="bg-gradient-to-r from-sky-600 via-cyan-600 to-indigo-700 bg-clip-text text-transparent">
            {title}
          </span>
        </h2>

        {paragraphs?.length > 0 &&
          paragraphs.map((p, i) => (
            <p key={i} className="mt-3 text-slate-700 leading-relaxed">
              {p}
            </p>
          ))}

        {bullets?.length > 0 && (
          <ul className="mt-4 space-y-2">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-1 inline-block h-2 w-2 rounded-full bg-sky-500 shrink-0" />
                <span className="text-slate-700">{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Image */}
      <div className={`${reverse ? "lg:order-1" : ""}`}>
        <div className="relative group">
          <div className="absolute -inset-0.5 rounded-3xl bg-gradient-to-br from-sky-400/40 via-cyan-400/40 to-indigo-500/40 blur-md opacity-70 group-hover:opacity-100 transition-opacity" />
          <div className="relative rounded-3xl overflow-hidden ring-1 ring-slate-200 bg-white/80">
            <img
              src={image}
              alt={imageAlt}
              className={`w-full h-72 sm:h-80 md:h-96 ${fitClass} ${imageClassName}`}
              style={imageStyle}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  const [current, setCurrent] = useState(0);
  const [hovering, setHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  }, []);

  // Preload slides safely
  useEffect(() => {
    if (typeof window === "undefined") return;
    slides.forEach((src) => {
      const img = new window.Image();
      img.src = src;
    });
  }, []);

  // Pause autoplay when tab hidden
  useEffect(() => {
    const onVis = () => setIsVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Reliable autoplay with single timeout
  useEffect(() => {
    if (prefersReducedMotion || hovering || !isVisible) return;
    const id = setTimeout(() => {
      setCurrent((p) => (p + 1) % slides.length);
    }, 3200);
    return () => clearTimeout(id);
  }, [current, hovering, prefersReducedMotion, isVisible]);

  const goPrev = () => setCurrent((p) => (p - 1 + slides.length) % slides.length);
  const goNext = () => setCurrent((p) => (p + 1) % slides.length);
  const onKey = (e) => {
    if (e.key === "ArrowLeft") goPrev();
    if (e.key === "ArrowRight") goNext();
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-[#F7FAFF] via-[#F3F6FB] to-[#F9FBFF]">
      {/* ===================== DESKTOP HERO (lg+): BIG FULL-BLEED SLIDER ===================== */}
      <section className="relative w-full hidden lg:block">
        <div
          className="relative w-full h-[78vh] xl:h-[88vh] overflow-hidden"
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          onKeyDown={onKey}
          tabIndex={0}
          aria-label="GrowMax hero slider (desktop)"
        >
          {/* Glow border */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -inset-1 rounded-[28px] mx-6 my-6 bg-gradient-to-br from-sky-300/35 via-cyan-300/35 to-indigo-400/35 blur" />
          </div>

          {/* Slides */}
          {slides.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`GrowMax banner ${i + 1}`}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
                current === i ? "opacity-100" : "opacity-0"
              }`}
              loading={i === 0 ? "eager" : "lazy"}
              fetchpriority={i === 0 ? "high" : "low"}
              draggable={false}
            />
          ))}

          {/* Bottom gradient */}
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />

          {/* Floating Info Card */}
          <div className="absolute left-12 bottom-12 max-w-2xl">
            <div className="rounded-3xl bg-white/85 backdrop-blur-xl ring-1 ring-white/60 shadow-[0_20px_60px_rgba(2,6,23,.28)] p-7">
              <p className="text-[11px] tracking-[0.28em] uppercase text-sky-700 font-semibold">
                Unlock Your Potential. Build Your Success.
              </p>
              <h1 className="mt-2 text-4xl xl:text-5xl font-extrabold leading-tight">
                <span className="bg-gradient-to-r from-sky-700 via-cyan-700 to-indigo-800 bg-clip-text text-transparent">
                  About GrowMax
                </span>
              </h1>
              <p className="mt-3 text-slate-700 leading-relaxed text-base">
                Practical, industry-backed programs led by <strong>Baba V. Ohol</strong> to help graduates,
                professionals and entrepreneurs thrive.
              </p>
              <div className="mt-4 flex flex-wrap gap-2.5">
                {[
                  "Confidence & clarity",
                  "Leadership & technical skills",
                  "Safety & quality",
                  "Real-world solutions",
                ].map((chip) => (
                  <span
                    key={chip}
                    className="text-sm px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 ring-1 ring-sky-200"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Arrows */}
          <button
            onClick={goPrev}
            aria-label="Previous slide"
            className="absolute left-6 top-1/2 -translate-y-1/2 grid place-items-center
                       h-12 w-12 rounded-full bg-white/85 backdrop-blur
                       text-slate-800 shadow ring-1 ring-slate-200
                       hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
              <path
                fillRule="evenodd"
                d="M12.78 4.22a.75.75 0 010 1.06L8.06 10l4.72 4.72a.75.75 0 11-1.06 1.06l-5.25-5.25a.75.75 0 010-1.06l5.25-5.25a.75.75 0 011.06 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <button
            onClick={goNext}
            aria-label="Next slide"
            className="absolute right-6 top-1/2 -translate-y-1/2 grid place-items-center
                       h-12 w-12 rounded-full bg-white/85 backdrop-blur
                       text-slate-800 shadow ring-1 ring-slate-200
                       hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
              <path
                fillRule="evenodd"
                d="M7.22 15.78a.75.75 0 010-1.06L11.94 10 7.22 5.28a.75.75 0 111.06-1.06l5.25 5.25a.75.75 0 010 1.06l-5.25 5.25a.75.75 0 01-1.06 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Dots */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-2.5 rounded-full transition-all outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white ${
                  current === i
                    ? "bg-white w-6 shadow ring-1 ring-white/60"
                    : "bg-white/60 hover:bg-white/80 w-2.5"
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===================== MOBILE & TABLET (base…lg-1): SMALLER SLIDER ===================== */}
      <section className="relative lg:hidden">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-8">
          <div className="grid lg:grid-cols-[1.1fr_1fr] grid-cols-1 gap-8 md:gap-12">
            {/* Copy */}
            <div className="flex flex-col justify-center order-2 md:order-1">
              <p className="text-xs tracking-[0.3em] uppercase text-sky-600 font-semibold">
                Unlock Your Potential. Build Your Success.
              </p>
              <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold leading-tight">
                <span className="bg-gradient-to-r from-sky-700 via-cyan-700 to-indigo-800 bg-clip-text text-transparent">
                  About GrowMax
                </span>
              </h1>
              <p className="mt-3 text-slate-700 leading-relaxed">
                GrowMax is more than a training platform—it’s a gateway to personal and professional transformation.
                Spearheaded by <strong>Baba V. Ohol</strong>, we empower graduates, professionals, and entrepreneurs
                to thrive through practical, industry-backed programs.
              </p>

              <ul className="mt-4 space-y-2">
                {[
                  "Gain confidence and clarity in your role.",
                  "Develop leadership and technical skills that matter.",
                  "Enhance safety, quality, and operational efficiency.",
                  "Navigate challenges with innovative, real-world solutions.",
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
                    <span className="text-slate-700">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Slider */}
            <div className="order-1 md:order-2">
              <div
                className="relative aspect-[16/9] sm:aspect-[16/9] md:h-[340px] rounded-3xl overflow-hidden ring-1 ring-slate-200 bg-slate-100"
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                onKeyDown={onKey}
                tabIndex={0}
                aria-label="GrowMax hero slider (mobile & tablet)"
              >
                {slides.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`GrowMax banner ${i + 1}`}
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
                      current === i ? "opacity-100" : "opacity-0"
                    }`}
                    loading={i === 0 ? "eager" : "lazy"}
                  />
                ))}

                {/* Caption */}
                <div className="absolute left-0 right-0 bottom-0 p-4 sm:p-5 bg-gradient-to-t from-black/50 to-transparent">
                  <p className="text-white/95 text-sm sm:text-base font-medium">
                    Unlock Your Potential. Build Your Success.
                  </p>
                </div>

                {/* Dots */}
                <div className="absolute bottom-3 right-4 flex items-center gap-2">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrent(i)}
                      aria-label={`Go to slide ${i + 1}`}
                      className={`h-2.5 w-2.5 rounded-full transition-all ${
                        current === i
                          ? "bg-white shadow ring-1 ring-white/60 w-5"
                          : "bg-white/60 hover:bg-white/80"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Arrows (mobile/tablet) */}
              <div className="mt-3 flex items-center justify-end gap-2 pr-1">
                <button
                  onClick={goPrev}
                  aria-label="Previous slide"
                  className="grid place-items-center h-10 w-10 rounded-full bg-white/90 text-slate-800 shadow ring-1 ring-slate-200 hover:bg-white"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                    <path
                      fillRule="evenodd"
                      d="M12.78 4.22a.75.75 0 010 1.06L8.06 10l4.72 4.72a.75.75 0 11-1.06 1.06l-5.25-5.25a.75.75 0 010-1.06l5.25-5.25a.75.75 0 011.06 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <button
                  onClick={goNext}
                  aria-label="Next slide"
                  className="grid place-items-center h-10 w-10 rounded-full bg-white/90 text-slate-800 shadow ring-1 ring-slate-200 hover:bg-white"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                    <path
                      fillRule="evenodd"
                      d="M7.22 15.78a.75.75 0 010-1.06L11.94 10 7.22 5.28a.75.75 0 111.06-1.06l5.25 5.25a.75.75 0 010 1.06l-5.25 5.25a.75.75 0 01-1.06 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== CONTENT SECTIONS ===================== */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-16 space-y-16 md:space-y-24">
        <Section
          title="Who is Baba Ohol?"
          imageFit="contain"
          imageStyle={{
            borderRadius: "0px",
            transform: "scaleX(-1)",
            objectFit: "contain",
            objectPosition: "center",
          }}
          image={personImage1}
          imageAlt="Baba V. Ohol"
          paragraphs={[
            "Baba V. Ohol — Visionary Motivational Speaker & Corporate Trainer, Managing Director of OGCS Private Limited.",
            "Renowned for transformative sessions that inspire and empower teams across industries. With over three decades of experience, Baba blends practical insights with leadership excellence, making him a sought-after name in professional development.",
          ]}
        />

        <Section
          reverse
          title="Journey of Excellence"
          image={journy}
          imageAlt="Journey of Baba Ohol"
          paragraphs={[
            "From the construction industry to leading large-scale initiatives across roads, hydropower, and petrochemicals, Baba’s journey is defined by grit, precision, and results.",
            "His unique approach marries technical rigor with soft-skills mastery—driving teams to execute with confidence and clarity.",
          ]}
        />

        <Section
          title="Why Choose GrowMax"
          image={logo}
          imageFit="contain"
          imageStyle={{ borderRadius: "0px" }}
          imageAlt="Why GrowMax"
          bullets={[
            "Unmatched Expertise — Programs crafted by seasoned industry leaders.",
            "Tailored Solutions — Designed around your goals and context.",
            "Transformative Impact — Outcomes you can apply immediately.",
            "Comprehensive Range — From technical to leadership development.",
          ]}
        />

        <Section
          reverse
          title="Technical Skills Training"
          image={technical}
          imageAlt="Technical Skills Training"
          bullets={[
            "Master construction techniques: planning, QA/QC, safety protocols, documentation.",
            "Enhance efficiency: reduce cost, manage risk, meet deadlines.",
            "Growth Built Program: an intensive week covering excavation, formwork, quality control and more.",
          ]}
        />

        <Section
          title="Soft Skills Training"
          image={softskill}
          imageAlt="Soft Skills Training"
          bullets={[
            "Leadership Excellence: communicate with impact and make better decisions.",
            "Conflict Resolution: tools to manage stress and build trust.",
            "Confidence Booster Program: unlock presence, clarity, and growth.",
          ]}
        />

        <Section
          reverse
          title="Meet the Leader — Baba V. Ohol"
          image={leader}
          imageAlt="Meet the Leader"
          paragraphs={[
            "Visionary leader with 30+ years of experience managing complex, multi-disciplinary projects.",
            "Baba’s sessions translate experience into strategies that elevate performance, culture, and outcomes.",
          ]}
        />
      </section>
    </main>
  );
}























































// // src/pages/About.jsx
// import React, { useEffect, useState } from "react";

// // Assets
// import personImage1 from "../assets/sir2.png";
// import softskill from "../assets/softskills.jpg";
// import technical from "../assets/technical.png";
// import logo from "../assets/growmaxlogo11.png";
// import leader from "../assets/leadersir.jpeg";
// import journy from "../assets/journy.jpg";
// import banner1 from "../assets/banner1.jpeg";
// import banner2 from "../assets/banner2.jpeg";
// import Image from "../assets/Image.jpeg";
// import Award from "../assets/Award.jpeg";

// const slides = [banner1, banner2, Image, Award];

// function Section({
//   reverse = false,
//   title,
//   subtitle,
//   paragraphs = [],
//   bullets = [],
//   image,
//   imageAlt,
//   imageClassName = "",
//   imageStyle = {},
//   imageFit = "cover",
// }) {
//   const fitClass = imageFit === "contain" ? "object-contain" : "object-cover";
//   return (
//     <section
//       className={`grid items-center gap-8 md:gap-12 lg:gap-14 ${
//         reverse ? "lg:grid-cols-[1.1fr_1fr]" : "lg:grid-cols-[1fr_1.1fr]"
//       } grid-cols-1`}
//     >
//       {/* Text */}
//       <div className={`${reverse ? "lg:order-2" : ""}`}>
//         {subtitle && (
//           <p className="text-xs tracking-[0.25em] uppercase text-sky-600 font-semibold mb-2">
//             {subtitle}
//           </p>
//         )}
//         <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-tight">
//           <span className="bg-gradient-to-r from-sky-600 via-cyan-600 to-indigo-700 bg-clip-text text-transparent">
//             {title}
//           </span>
//         </h2>

//         {paragraphs?.length > 0 &&
//           paragraphs.map((p, i) => (
//             <p key={i} className="mt-3 text-slate-700 leading-relaxed">
//               {p}
//             </p>
//           ))}

//         {bullets?.length > 0 && (
//           <ul className="mt-4 space-y-2">
//             {bullets.map((b, i) => (
//               <li key={i} className="flex items-start gap-3">
//                 <span className="mt-1 inline-block h-2 w-2 rounded-full bg-sky-500 shrink-0" />
//                 <span className="text-slate-700">{b}</span>
//               </li>
//             ))}
//           </ul>
//         )}
//       </div>

//       {/* Image */}
//       <div className={`${reverse ? "lg:order-1" : ""}`}>
//         <div className="relative group">
//           <div className="absolute -inset-0.5 rounded-3xl bg-gradient-to-br from-sky-400/40 via-cyan-400/40 to-indigo-500/40 blur-md opacity-70 group-hover:opacity-100 transition-opacity" />
//           <div className="relative rounded-3xl overflow-hidden ring-1 ring-slate-200 bg-white/80">
//             <img
//               src={image}
//               alt={imageAlt}
//               // className="w-full h-72 sm:h-80 md:h-96 object-cover"object-center
//               className={`w-full h-72 sm:h-80 md:h-96  ${fitClass}  ${imageClassName}`}
//               style={imageStyle}
//               loading="lazy"
//             />
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// }

// export default function About() {
//   const [current, setCurrent] = useState(0);

//   useEffect(() => {
//     const id = setInterval(() => {
//       setCurrent((p) => (p + 1) % slides.length);
//     }, 3200);
//     return () => clearInterval(id);
//   }, []);

//   return (
//     <main className="min-h-screen bg-gradient-to-b from-[#F7FAFF] via-[#F3F6FB] to-[#F9FBFF]">
//       {/* HERO / SLIDER IN MOBILE VIEW*/}
//       <section className="relative">
//         <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-8">
//           <div className="grid lg:grid-cols-[1.1fr_1fr] grid-cols-1 gap-8 md:gap-12">
//             {/* Copy */}
//             <div className="flex flex-col justify-center order-2 lg:order-1">
//               <p className="text-xs tracking-[0.3em] uppercase text-sky-600 font-semibold">
//                 Unlock Your Potential. Build Your Success.
//               </p>
//               <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
//                 <span className="bg-gradient-to-r from-sky-700 via-cyan-700 to-indigo-800 bg-clip-text text-transparent">
//                   About GrowMax
//                 </span>
//               </h1>
//               <p className="mt-3 text-slate-700 leading-relaxed">
//                 GrowMax is more than a training platform—it’s a gateway to
//                 personal and professional transformation. Spearheaded by{" "}
//                 <strong>Baba V. Ohol</strong>, we empower graduates,
//                 professionals, and entrepreneurs to thrive through practical,
//                 industry-backed programs.
//               </p>

//               <ul className="mt-4 space-y-2">
//                 {[
//                   "Gain confidence and clarity in your role.",
//                   "Develop leadership and technical skills that matter.",
//                   "Enhance safety, quality, and operational efficiency.",
//                   "Navigate challenges with innovative, real-world solutions.",
//                 ].map((b, i) => (
//                   <li key={i} className="flex items-start gap-3">
//                     <span className="mt-1 h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
//                     <span className="text-slate-700">{b}</span>
//                   </li>
//                 ))}
//               </ul>
//             </div>

//             {/* Slider */}
//             <div className="order-1 lg:order-2">
//               <div className="relative aspect-[16/9] rounded-3xl overflow-hidden ring-1 ring-slate-200 bg-slate-100">
//                 {slides.map((src, i) => (
//                   <img
//                     key={i}
//                     src={src}
//                     alt={`GrowMax banner ${i + 1}`}
//                     className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
//                       current === i ? "opacity-100" : "opacity-0"
//                     }`}
//                     loading="lazy"
//                   />
//                 ))}
//                 {/* Caption */}
//                 <div className="absolute left-0 right-0 bottom-0 p-4 sm:p-5 bg-gradient-to-t from-black/50 to-transparent">
//                   <p className="text-white/95 text-sm sm:text-base font-medium">
//                     Unlock Your Potential. Build Your Success.
//                   </p>
//                 </div>

//                 {/* Dots */}
//                 <div className="absolute bottom-3 right-4 flex items-center gap-2">
//                   {slides.map((_, i) => (
//                     <button
//                       key={i}
//                       onClick={() => setCurrent(i)}
//                       aria-label={`Go to slide ${i + 1}`}
//                       className={`h-2.5 w-2.5 rounded-full transition-all ${
//                         current === i
//                           ? "bg-white shadow ring-1 ring-white/60 w-5"
//                           : "bg-white/60 hover:bg-white/80"
//                       }`}
//                     />
//                   ))}
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* CONTENT SECTIONS */}
//       <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-16 space-y-16 md:space-y-24">
//         <Section
//           title="Who is Baba Ohol?"
//           imageFit="contain"
//           imageStyle={{
//             borderRadius: "0px",
//             transform: "scaleX(-1)", // 👈 this flips the image horizontally
//             objectFit: "contain", // 👈 ensures full image fits
//             objectPosition: "center", // 👈 keeps it centered
//           }}
//           image={personImage1}
//           imageAlt="Baba V. Ohol"
//           paragraphs={[
//             "Baba V. Ohol — Visionary Motivational Speaker & Corporate Trainer, Managing Director of OGCS Private Limited.",
//             "Renowned for transformative sessions that inspire and empower teams across industries. With over three decades of experience, Baba blends practical insights with leadership excellence, making him a sought-after name in professional development.",
//           ]}
//         />

//         <Section
//           reverse
//           title="Journey of Excellence"
//           image={journy}
//           imageAlt="Journey of Baba Ohol"
//           paragraphs={[
//             "From the construction industry to leading large-scale initiatives across roads, hydropower, and petrochemicals, Baba’s journey is defined by grit, precision, and results.",
//             "His unique approach marries technical rigor with soft-skills mastery—driving teams to execute with confidence and clarity.",
//           ]}
//         />

//         <Section
//           title="Why Choose GrowMax"
//           image={logo}
//           imageFit="contain"
//           imageStyle={{ borderRadius: "0px" }}
//           imageAlt="Why GrowMax"
//           bullets={[
//             "Unmatched Expertise — Programs crafted by seasoned industry leaders.",
//             "Tailored Solutions — Designed around your goals and context.",
//             "Transformative Impact — Outcomes you can apply immediately.",
//             "Comprehensive Range — From technical to leadership development.",
//           ]}
//         />

//         <Section
//           reverse
//           title="Technical Skills Training"
//           image={technical}
//           imageAlt="Technical Skills Training"
//           bullets={[
//             "Master construction techniques: planning, QA/QC, safety protocols, documentation.",
//             "Enhance efficiency: reduce cost, manage risk, meet deadlines.",
//             "Growth Built Program: an intensive week covering excavation, formwork, quality control and more.",
//           ]}
//         />

//         <Section
//           title="Soft Skills Training"
//           image={softskill}
//           imageAlt="Soft Skills Training"
//           bullets={[
//             "Leadership Excellence: communicate with impact and make better decisions.",
//             "Conflict Resolution: tools to manage stress and build trust.",
//             "Confidence Booster Program: unlock presence, clarity, and growth.",
//           ]}
//         />

//         <Section
//           reverse
//           title="Meet the Leader — Baba V. Ohol"
//           image={leader}
//           imageAlt="Meet the Leader"
//           paragraphs={[
//             "Visionary leader with 30+ years of experience managing complex, multi-disciplinary projects.",
//             "Baba’s sessions translate experience into strategies that elevate performance, culture, and outcomes.",
//           ]}
//         />
//       </section>
//     </main>
//   );
// }

